import { useState, useEffect } from 'react';
import { Settings, Type, Eye, MousePointer2, X, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

interface AccessibilitySettings {
  fontSize: 'normal' | 'large' | 'xlarge';
  highContrast: boolean;
  reduceMotion: boolean;
  underlineLinks: boolean;
  highlightFocus: boolean;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 'normal',
  highContrast: false,
  reduceMotion: false,
  underlineLinks: false,
  highlightFocus: false,
};

export function ADAWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    // Load from localStorage
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('ada-settings');
        if (saved) {
          return { ...defaultSettings, ...JSON.parse(saved) };
        }
      } catch {
        // Ignore parse errors
      }
    }
    return defaultSettings;
  });

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    // Font size
    root.classList.remove('font-size-normal', 'font-size-large', 'font-size-xlarge');
    root.classList.add(`font-size-${settings.fontSize}`);

    // High contrast
    if (settings.highContrast) {
      body.classList.add('high-contrast');
    } else {
      body.classList.remove('high-contrast');
    }

    // Reduce motion
    if (settings.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Underline links
    if (settings.underlineLinks) {
      body.classList.add('underline-links');
    } else {
      body.classList.remove('underline-links');
    }

    // Highlight focus
    if (settings.highlightFocus) {
      body.classList.add('highlight-focus');
    } else {
      body.classList.remove('highlight-focus');
    }

    // Save to localStorage
    localStorage.setItem('ada-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <>
      {/* Floating Button - Positioned to avoid conflict with sticky CTA bar */}
      <div
        className="fixed right-4 z-[5000]"
        style={{
          right: 'calc(max(1rem, env(safe-area-inset-right)) + 4.25rem)',
          bottom: 'calc(var(--sticky-cta-offset, 0px) + 1rem)',
          pointerEvents: 'none',
        }}
      >
        <button
          onClick={() => setIsOpen(true)}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#e69138] text-white shadow-lg transition-all hover:bg-[#d17f2a] hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2"
          style={{
            pointerEvents: 'auto',
            position: 'relative',
          }}
          aria-label="Open accessibility settings"
          title="Accessibility Settings"
        >
          <Settings className="h-6 w-6" />
          <span className="sr-only">Open accessibility settings panel</span>
        </button>
      </div>

      {/* Settings Panel */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[5001] bg-black/50 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      <div
        className={cn(
          'fixed right-4 z-[5002] w-[calc(100vw-2rem)] max-w-md rounded-lg bg-white shadow-2xl transition-all duration-300',
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        )}
        style={{
          right: 'max(1rem, env(safe-area-inset-right))',
          bottom: 'calc(var(--sticky-cta-offset, 0px) + 5.5rem)',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ada-settings-title"
        aria-describedby="ada-settings-description"
      >
        <div className="p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 id="ada-settings-title" className="text-xl font-bold text-gray-900">
                Accessibility Settings
              </h2>
              <p id="ada-settings-description" className="sr-only">
                Customize your browsing experience with text size, contrast, motion, and focus options
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-[#e69138] rounded"
              aria-label="Close accessibility settings"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Font Size */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Type className="h-5 w-5 text-[#e69138]" />
                <label className="font-semibold text-gray-900">Text Size</label>
              </div>
              <div className="flex gap-2">
                {(['normal', 'large', 'xlarge'] as const).map((size) => (
                  <button
                    key={size}
                    onClick={() => updateSetting('fontSize', size)}
                    className={cn(
                      'flex-1 rounded-lg border-2 px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2',
                      settings.fontSize === size
                        ? 'border-[#e69138] bg-[#e69138] text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    )}
                    aria-pressed={settings.fontSize === size}
                    aria-label={`Set text size to ${size === 'normal' ? 'normal' : size === 'large' ? 'large' : 'extra large'}`}
                  >
                    {size === 'normal' ? 'Normal' : size === 'large' ? 'Large' : 'Extra Large'}
                  </button>
                ))}
              </div>
            </div>

            {/* High Contrast */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Eye className="h-5 w-5 text-[#e69138]" />
                <label className="font-semibold text-gray-900">High Contrast</label>
              </div>
                <button
                  onClick={() => updateSetting('highContrast', !settings.highContrast)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg border-2 p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2',
                    settings.highContrast
                      ? 'border-[#e69138] bg-[#e69138]/10'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  )}
                  aria-pressed={settings.highContrast}
                  aria-label={settings.highContrast ? 'Disable high contrast mode' : 'Enable high contrast mode'}
                >
                <span className="text-sm text-gray-700">
                  Increase contrast for better visibility
                </span>
                {settings.highContrast && (
                  <Check className="h-5 w-5 text-[#e69138]" />
                )}
              </button>
            </div>

            {/* Reduce Motion */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <MousePointer2 className="h-5 w-5 text-[#e69138]" />
                <label className="font-semibold text-gray-900">Reduce Motion</label>
              </div>
                <button
                  onClick={() => updateSetting('reduceMotion', !settings.reduceMotion)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg border-2 p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2',
                    settings.reduceMotion
                      ? 'border-[#e69138] bg-[#e69138]/10'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  )}
                  aria-pressed={settings.reduceMotion}
                  aria-label={settings.reduceMotion ? 'Enable animations' : 'Reduce motion and animations'}
                >
                <span className="text-sm text-gray-700">
                  Minimize animations and transitions
                </span>
                {settings.reduceMotion && (
                  <Check className="h-5 w-5 text-[#e69138]" />
                )}
              </button>
            </div>

            {/* Underline Links */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Type className="h-5 w-5 text-[#e69138]" />
                <label className="font-semibold text-gray-900">Underline Links</label>
              </div>
                <button
                  onClick={() => updateSetting('underlineLinks', !settings.underlineLinks)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg border-2 p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2',
                    settings.underlineLinks
                      ? 'border-[#e69138] bg-[#e69138]/10'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  )}
                  aria-pressed={settings.underlineLinks}
                  aria-label={settings.underlineLinks ? 'Remove underlines from links' : 'Always underline links'}
                >
                <span className="text-sm text-gray-700">
                  Always underline links for better visibility
                </span>
                {settings.underlineLinks && (
                  <Check className="h-5 w-5 text-[#e69138]" />
                )}
              </button>
            </div>

            {/* Highlight Focus */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <MousePointer2 className="h-5 w-5 text-[#e69138]" />
                <label className="font-semibold text-gray-900">Highlight Focus</label>
              </div>
                <button
                  onClick={() => updateSetting('highlightFocus', !settings.highlightFocus)}
                  className={cn(
                    'flex w-full items-center justify-between rounded-lg border-2 p-4 transition-colors focus:outline-none focus:ring-2 focus:ring-[#e69138] focus:ring-offset-2',
                    settings.highlightFocus
                      ? 'border-[#e69138] bg-[#e69138]/10'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  )}
                  aria-pressed={settings.highlightFocus}
                  aria-label={settings.highlightFocus ? 'Disable enhanced focus indicators' : 'Enable enhanced focus indicators'}
                >
                <span className="text-sm text-gray-700">
                  Enhance focus indicators for keyboard navigation
                </span>
                {settings.highlightFocus && (
                  <Check className="h-5 w-5 text-[#e69138]" />
                )}
              </button>
            </div>

            {/* Reset Button */}
            <Button
              onClick={resetSettings}
              variant="outline"
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Reset to Defaults
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
